import { observer } from 'mobx-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Fragment, useContext, useMemo } from 'react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { storesContext } from '../../stores/storesContext';
import SeasonalStrategyChart from '../charts/SeasonalStrategyChart';
import WeeklySoilMoistureChart from '../charts/WeeklySoilMoistureChart';
import IrrigationLegend from './IrrigationLegend';
import SummaryMap from './maps/SummaryMap';
import LastWeekRain from './tables/LastWeekRain';
import LastWeekTable from './tables/LastWeekTable';
import RecommendationTable from './tables/RecommendationTable';
import SeasonToDateTable from './tables/SeasonToDateTable';

const TerraprobeSite = ({
  selectedSite,
  siteSummaries,
  siteReadings,
  siteStrategies,
  isPrinting,
  selectSite
}) => {
  const userStore = useContext(storesContext);

  /**
   * Returns the summary for a site.
   *
   * The summary has summary has the data we need to calculate the current
   * status of the site.
   *
   * @param {object} site
   * @returns {(object|null)}
   */
  const siteSummary = (site) =>
    siteSummaries.find(
      (siteSummary) => siteSummary.site_id === Number(site.site_number)
    );

  /**
   * Returns all probe readings for the selected site.
   *
   * @returns {(Array|null)}
   */
  const selectedSiteProbeReadings = useMemo(() => {
    if (selectedSite)
      return siteReadings[selectedSite.id]
        ? siteReadings[selectedSite.id].filter(
            (reading) => reading.type === 'Probe'
          )
        : null;
    else return null;
  }, [siteReadings, selectedSite]);

  /**
   * Returns the latest probe reading for the selected site.
   *
   * @returns {(object|null)}
   */
  const latestWeekReading = useMemo(() => {
    return selectedSiteProbeReadings && selectedSiteProbeReadings.length > 0
      ? selectedSiteProbeReadings[0]
      : null;
  }, [selectedSiteProbeReadings]);

  return (
    <>
      {Object.keys(siteReadings).some(
        (s) => Number(s) === Number(selectedSite.id)
      ) ? (
        <Fragment>
          <Row className='my-2'>
            <Col
              xl={6}
              lg={6}
              md={12}
              xs={isPrinting ? 6 : 12}
              className='my-2'
            >
              <Card>
                <Card.Header>
                  <b>Recommendation</b>
                </Card.Header>
                <Card.Body>
                  {latestWeekReading ? latestWeekReading.comment : null}
                </Card.Body>
              </Card>
            </Col>
            <Col
              xl={6}
              lg={6}
              md={12}
              xs={isPrinting ? 6 : 12}
              className='my-2'
            >
              <Card>
                <Card.Header>
                  <b>Summary Since Last Reading</b>
                </Card.Header>
                <Card.Body>
                  Last Reading:{' '}
                  <b>
                    {latestWeekReading
                      ? moment(latestWeekReading['date'], 'DD-MM-YYYY').format(
                          'dddd, MMMM D, YYYY'
                        )
                      : 'N/A'}
                  </b>
                  <br />
                  Crop use 7 days (Forecast):{' '}
                  <b>
                    {latestWeekReading && latestWeekReading.weekly_edwu !== null
                      ? `${latestWeekReading.weekly_edwu}mm`
                      : 'N/A'}
                  </b>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col
              xl={6}
              lg={6}
              md={12}
              xs={isPrinting ? 6 : 12}
              className='my-2'
            >
              <Card>
                <Card.Header>
                  <b>Current State</b>
                </Card.Header>
                <Card.Body>
                  {selectedSiteProbeReadings &&
                  selectedSiteProbeReadings.length > 0 ? (
                    <Row>
                      <Col>
                        <WeeklySoilMoistureChart
                          readingData={siteReadings[selectedSite.id]}
                        />
                        <small className='d-block text-center'>
                          Root Zone: 0 - {latestWeekReading.rz1_bottom} cm
                        </small>
                      </Col>
                    </Row>
                  ) : (
                    'N/A'
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col
              xl={6}
              lg={6}
              md={12}
              xs={isPrinting ? 6 : 12}
              className='my-2'
            >
              <Card>
                <Card.Header>
                  <b>Schedule</b>
                </Card.Header>
                <Card.Body>
                  <RecommendationTable
                    readingData={siteReadings[selectedSite.id]}
                    site={selectedSite}
                  />
                  <Row>
                    <Col className='text-center'>
                      <Button
                        variant='outline-info'
                        as={Link}
                        to='/programming-guide'
                      >
                        Property Programming Guide
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col className='my-2'>
              <Card>
                <Card.Header>
                  <b>Last Week</b>
                </Card.Header>
                <Card.Body>
                  {selectedSiteProbeReadings &&
                  selectedSiteProbeReadings.length > 0 ? (
                    <LastWeekTable
                      readingData={siteReadings[selectedSite.id]}
                      site={selectedSite}
                    />
                  ) : (
                    'N/A'
                  )}
                  <LastWeekRain readingData={siteReadings[selectedSite.id]} />
                </Card.Body>
              </Card>
            </Col>

            <Col className='my-2'>
              <Card>
                <Card.Header>
                  <b>Season To Date</b>
                </Card.Header>
                <Card.Body>
                  {selectedSiteProbeReadings &&
                  selectedSiteProbeReadings.length > 0 ? (
                    <SeasonToDateTable
                      readingData={siteReadings[selectedSite.id]}
                      className='mb-0'
                    />
                  ) : (
                    'N/A'
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className='my-2 page-break'>
            <Col>
              <Card>
                <Card.Header>
                  <b>Seasonal Summary &amp; Strategy</b>
                </Card.Header>
                <Card.Body>
                  {selectedSiteProbeReadings &&
                  selectedSiteProbeReadings.length > 0 ? (
                    <SeasonalStrategyChart
                      readingData={siteReadings[selectedSite.id]}
                      strategyData={siteStrategies[selectedSite.id]}
                      siteSummary={siteSummary(selectedSite)}
                    />
                  ) : (
                    'N/A'
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className='my-2'>
            <Col className='my-2'>
              <Card>
                <Card.Header>
                  <b>Map</b>
                </Card.Header>
                <Card.Body>
                  <IrrigationLegend className='mb-4 text-center' />
                  <Row style={{ height: '500px', width: '100%' }}>
                    <Col>
                      <SummaryMap
                        propertySummaries={[
                          {
                            loading: false,
                            property:
                              userStore.propertiesStore.selectedProperty,
                            results: {
                              sites: siteSummaries
                            }
                          }
                        ]}
                        selectSite={selectSite}
                        selectedSite={selectedSite}
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Fragment>
      ) : null}
    </>
  );
};

TerraprobeSite.propTypes = {
  selectedSite: PropTypes.object,
  siteSummaries: PropTypes.array,
  siteReadings: PropTypes.object,
  siteStrategies: PropTypes.object,
  isPrinting: PropTypes.bool,
  selectSite: PropTypes.func
};

export default observer(TerraprobeSite);

